"use client";

import React from "react";
import Table, { Column } from "../ui/Table";
import MoreButton from "./MoreButton";

interface AdvisoryData {
  id: string;
  date: string;
  product: string;
  versions: string;
  type: string;
  severity: "critical" | "high" | "medium" | "low";
  vulnerabilityId: string;
  url: string;
}

const SecurityAdvisories = () => {
  const data: AdvisoryData[] = [
    {
      id: "1",
      date: "15/09/2020",
      product: "Trend Micro ServerProtect",
      versions: "<3.0",
      type: "Command Injection",
      severity: "critical",
      vulnerabilityId: "CVE-2020-24561",
      url: "/trend_micro_server_protect_linux_command_injection",
    },
    {
      id: "2",
      date: "10/01/2020",
      product: "Oracle EBS",
      versions: "<12.2.6",
      type: "Local File Inclusion",
      severity: "medium",
      vulnerabilityId: "CVE-2020-14826",
      url: "/oracle_EBS_local_file_inclusion",
    },
    {
      id: "3",
      date: "07/02/2004",
      product: "Internet Explorer",
      versions: "5.x up to SP3 inclusive and up to 6.1 SP1 inclusive",
      type: "Heap overflow",
      severity: "critical",
      vulnerabilityId: "N/A",
      url: "/microsoft_internet_explorer_heap_memory_corruption_vulnerability",
    },
    {
      id: "4",
      date: "09/12/2003",
      product: "Gordano GMS",
      versions: "version 9, build 3138",
      type: "Remote DoS, Information Disclosure",
      severity: "medium",
      vulnerabilityId: "N/A",
      url: "/gordano_GMS_multiple_vulnerabilities",
    },
    {
      id: "5",
      date: "09/12/2003",
      product: "602Pro LAN SUITE",
      versions: "2003.0.3.0828",
      type: "Sensitive Files Exposure, Arbitrary File Reading",
      severity: "high",
      vulnerabilityId: "N/A",
      url: "/602ProLanSuite_multiple_vulnerabilities",
    },
    {
      id: "6",
      date: "07/10/2003",
      product: "mIRC",
      versions: "<6.1",
      type: "Buffer overflow",
      severity: "critical",
      vulnerabilityId: "CVE-2003-1336",
      url: "/mIRC_buffer_overflow_in_IRC_protocol",
    },
    {
      id: "7",
      date: "23/09/2003",
      product: "TCLHttpd",
      versions: "<3.4.2",
      type: "Arbitrary Directory Browsing, XSS",
      severity: "high",
      vulnerabilityId: "N/A",
      url: "/TCLHttpd_multiple_vulnerabilities",
    },
    {
      id: "8",
      date: "22/09/2003",
      product: "Savant Web Server",
      versions: "3.1",
      type: "DoS",
      severity: "medium",
      vulnerabilityId: "N/A",
      url: "/savant_web_Server_multiple_vulnerabilities",
    },
    {
      id: "9",
      date: "09/01/2003",
      product: "FTGate Pro",
      versions: "1.2, build 1331",
      type: "Information Disclosure, Username and Password exposures",
      severity: "high",
      vulnerabilityId: "N/A",
      url: "/FTGate_pro_multiple_vulnerabilities",
    },
    {
      id: "10",
      date: "28/05/2002",
      product: "Yahoo Messenger",
      versions: "5, 0, 0, 1061",
      type: "Buffer Overflow",
      severity: "critical",
      vulnerabilityId: "CVE-2002-0031",
      url: "/yahoo_messenger_multiple_vulnerabilities",
    },
    {
      id: "11",
      date: "02/04/2002",
      product: "Hosting Controller",
      versions: "<1.4.1",
      type: "Browsing Non-public Directories Allowed, Dot-Dot-Slash bug and autosignup/dsp_newwebadmin.asp",
      severity: "critical",
      vulnerabilityId: "CVE-2002-0466",
      url: "/hosting_controller_multiple_vulnerabilities",
    },
    {
      id: "12",
      date: "02/04/2002",
      product: "Hosting Controller (II)",
      versions: "<1.4.1",
      type: "Unauthorized file editing, Unauthorized folder management",
      severity: "critical",
      vulnerabilityId: "CVE-2002-0466",
      url: "/hosting_controller_ii_multiple_vulnerabilities",
    },
  ];

  const columns: Record<string, Column<AdvisoryData>> = {
    date: {
      id: "date",
      name: "Date",
      show: true,
      width: 145,
      render: (rowData: AdvisoryData) => (
        <span className="text-white font-normal">{rowData.date}</span>
      ),
    },
    product: {
      id: "product",
      name: "Product",
      show: true,
      width: 192,
    },
    versions: {
      id: "versions",
      name: "Versions",
      show: true,
      width: 251,
    },
    type: {
      id: "type",
      name: "Type",
      show: true,
      width: 216,
    },
    severity: {
      id: "severity",
      name: "Severity",
      show: true,
      width: 130,
      align: "center",
      render: (rowData: AdvisoryData) => {
        const colorClass =
          rowData.severity === "critical"
            ? "text-[#EC1938]"
            : rowData.severity === "high"
            ? "text-[#FFBC3B]"
            : rowData.severity === "medium"
            ? "text-[#3EAFF0]"
            : "text-[#00C7CD]";
        return (
          <span
            className={`font-normal text-[14px] sm:text-[16px] leading-[24px] sm:leading-[28px] tracking-[0.5px] ${colorClass}`}
          >
            {rowData.severity.charAt(0).toUpperCase() + rowData.severity.slice(1)}
          </span>
        );
      },
    },
    vulnerabilityId: {
      id: "vulnerabilityId",
      name: "Vulnerability ID",
      show: true,
      width: 198,
      render: (rowData: AdvisoryData) => (
        <span className="text-white font-normal">
          {rowData.vulnerabilityId}
        </span>
      ),
    },
    action: {
      id: "action",
      name: "Action",
      show: true,
      width: 168,
      render: (rowData: AdvisoryData) => (
        <MoreButton href={`/resources/security-advisories${rowData.url}`} />
      ),
    },
  };

  return (
    <div className="relative w-full px-4 sm:px-6 lg:px-8 pt-2 pb-12 overflow-hidden">
      <div className="max-w-[1300px] mx-auto relative z-10">
        {/* Table */}
        <Table
          columns={columns}
          data={data}
          enableCollapse={false}
          customClassName=""
        />
      </div>
    </div>
  );
};

export default SecurityAdvisories;
